// Singleton-Store für die Tasks-Status-Card auf der Übersicht.
// Folgt dem Pattern von security.svelte.ts:
//   - Initial-Fetch von /v2-api/tasks/summary beim Boot
//   - WS-Subscription auf "events"-Channel; bei jedem Event mit
//     domain="tasks" wird der Summary refresht
//
// Die Tasks-Domain emittiert Events erst beim Run-Abschluss
// (G27.2: task_finished | task_failed). "Aktuell läuft N" steckt
// im Action-Tracker, nicht hier — die Card zeigt das Ergebnis-
// Bild der letzten 24 h.

import { SvelteMap } from "svelte/reactivity";
import { registerRevalidator } from "./revalidate.svelte";

import { loadPersisted, persist } from "./persistent-summary.svelte";
import { subscribe as wsSubscribe } from "./ws-hub.svelte";
import { istRoutineFrame } from "./events-frame";

/** Ein Task, dessen letzter Lauf fehlgeschlagen und noch offen ist. */
export interface FailingTask {
  entity: string; // "task:<id>"
  name: string;
  ts: string;
  /** event_id: die events-Zeile hinter diesem Fehlschlag. Damit
   *  quittiert die Meldungs-Seite DAS EREIGNIS statt nur lokal — und
   *  Dashboard-Karte, Tasks-Seite und Meldungen zeigen denselben
   *  Stand. */
  event_id?: number;
  summary: string;
}

export interface TasksSummary {
  status: "ok" | "warning" | "problem";
  currently_failing_unacked: number;
  /** Wieviele Runs gerade laufen. Fuer die Statusleiste. */
  running_now: number;
  failed_last_1h: number;
  failed_last_24h: number;
  ok_last_24h: number;
  last_failure_at?: string;
  last_failure_task?: string;
  last_failure_summary?: string;
  /** Die Tasks hinter currently_failing_unacked — der JETZT-Zustand.
   *  Wer „was ist gerade kaputt" anzeigt (Warn-Dreieck, Meldungen),
   *  nimmt diese Liste, NICHT failed_last_24h: der Zähler blieb einen
   *  ganzen Tag stehen, obwohl der Task längst wieder grün war. */
  currently_failing?: FailingTask[];
  // Trajektorie der Failures pro Bucket über 24 h. Optional —
  // bei Fetch-Fehler bleibt die Card ohne Spark-Line.
  spark?: { values: number[] };
}

interface TimeseriesPoint {
  bucket: string;
  value: number;
}

interface EventsChannelFrame {
  domain?: string;
  type?: string;
}

const STORAGE_KEY = "tasks";

const _summary = new SvelteMap<string, TasksSummary>();
let _unsubscribe: (() => void) | undefined;
let _started = false;

async function refreshSummary(mitSpark = true): Promise<void> {
  try {
    const r = await fetch("/v2-api/tasks/summary", { credentials: "include" });
    if (!r.ok) return;
    const s = (await r.json()) as TasksSummary;
    // ERST den Stand setzen, DANN die Spark-Line nachreichen.
    //
    // Vorher hing das Sichtbarwerden am Spark-Fetch: nach "Alle
    // gelesen" blieb die Meldungs-Zeile stehen, bis die Zeitreihe
    // ueber die events-Tabelle zurueckkam. Die ist meist schnell, aber
    // nicht immer -- und genau daraus wird ein "manchmal dauert das
    // ewig" (Tim, 01.09.2026). Die Spark ist ein Zusatz; sie darf den
    // Zustand nicht aufhalten.
    const vorher = _summary.get("current");
    s.spark = vorher?.spark;
    _summary.set("current", s);
    persist(STORAGE_KEY, s);
    if (!mitSpark) return;
    const spark = await fetchSpark();
    if (spark) {
      const jetzt = _summary.get("current");
      // Nur setzen, wenn inzwischen kein neuerer Stand kam.
      if (jetzt === s) {
        _summary.set("current", { ...s, spark });
        persist(STORAGE_KEY, { ...s, spark });
      }
    }
  } catch {
    // ignore — WS will catch up on the next tick
  }
}

// Trailing 24 h failure-rate als Spark-Line. Eigene fetch-Funktion
// damit der Summary-Endpoint klein bleibt und das Frontend bei
// Fehler ohne Spark weiterrendert.
async function fetchSpark(): Promise<{ values: number[] } | undefined> {
  try {
    const r = await fetch(
      "/v2-api/events/timeseries?domain=tasks&type=task_failed&hours=24",
      { credentials: "include" },
    );
    if (!r.ok) return undefined;
    const points = (await r.json()) as TimeseriesPoint[];
    if (points.length === 0) return undefined;
    return { values: points.map((p) => p.value) };
  } catch {
    return undefined;
  }
}

// Lauf-Frames kommen im Takt der Waechter (~10 pro Minute). Die Summary
// (laufend, ok in 24 h) folgt ihnen gedrosselt: hoechstens ein Abruf je
// SUMMARY_ABSTAND_MS. Die 24-h-Fehlerkurve aendert sich nur bei einem
// gespeicherten Frame (Fehler oder Erfolg nach Fehler), nicht bei
// Routine-Laeufen (events-frame.ts).
const SUMMARY_ABSTAND_MS = 3_000;
let _summaryTimer: ReturnType<typeof setTimeout> | undefined;
let _sparkFaellig = false;

function onEventFrame(data: unknown): void {
  const ev = data as EventsChannelFrame | undefined;
  if (!ev || ev.domain !== "tasks") return;
  // task_started gehört dazu, sonst erfährt die Oberfläche nur vom
  // ENDE eines Laufs -- und "läuft gerade" stünde dauerhaft auf 0
  // (Tim 18.08.). Das Start-Signal kommt nur über den Hub, es steht
  // nicht in der events-Tabelle.
  if (
    ev.type !== "task_started" &&
    ev.type !== "task_finished" &&
    ev.type !== "task_failed"
  )
    return;
  if (!istRoutineFrame(data)) _sparkFaellig = true;
  if (_summaryTimer) return;
  _summaryTimer = setTimeout(() => {
    _summaryTimer = undefined;
    const mitSpark = _sparkFaellig;
    _sparkFaellig = false;
    void refreshSummary(mitSpark);
  }, SUMMARY_ABSTAND_MS);
}

export function startTasksSummaryStream(): void {
  if (_started) return;
  _started = true;
  registerRevalidator(refreshSummary);
  // SWR: letzten Snapshot sofort einspielen, damit die Tasks-Card beim
  // Reload nicht erst auf den Initial-Fetch warten muss. WS-Push haelt
  // den Wert danach live.
  const cached = loadPersisted<TasksSummary>(STORAGE_KEY);
  if (cached) _summary.set("current", cached);
  void refreshSummary().then(() => {
    if (!_started) return;
    _unsubscribe = wsSubscribe("events", onEventFrame);
  });
}

export function stopTasksSummaryStream(): void {
  _started = false;
  if (_summaryTimer) {
    clearTimeout(_summaryTimer);
    _summaryTimer = undefined;
  }
  if (_unsubscribe) {
    _unsubscribe();
    _unsubscribe = undefined;
  }
  _summary.clear();
}

/** refreshTasksSummary holt den Stand sofort neu.
 *
 *  Gebraucht nach dem Quittieren einer Task-Meldung: das Acken schreibt
 *  nur `acked_at` in die events-Zeile und loest KEIN neues Ereignis aus
 *  -- der WS-Kanal schweigt also, und die Karte stuende bis zur
 *  naechsten Revalidierung weiter auf rot. */
export async function refreshTasksSummary(): Promise<void> {
  await refreshSummary();
}

export function getTasksSummary(): TasksSummary | undefined {
  return _summary.get("current");
}
